/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	generateSimplestList,
	readOnlyAttribute,
	DefaultSplitDataRenderType,
	SplitData,
	PlanAccessLevel,
	IPlanAccessFields,
} from '@touchplan/common';

export interface ILegacyIPlanAccessFields {
	readOnlyMode?: boolean;
}

export class PlanAccess extends SplitData<IPlanAccessFields, DefaultSplitDataRenderType> {
	protected useInPlace = true;

	protected attributeList = Object.assign(
		generateSimplestList(['accessLevel', 'accessModifiedBy', 'accessModifiedAt', 'excludeFromDashboard']),
		{
			backendStatus: readOnlyAttribute,
			backendStatusAt: readOnlyAttribute,
		}
	);

	constructor(id: string) {
		super(id, DefaultSplitDataRenderType.database);
	}

	public generateDatabaseOutput(renderType?: DefaultSplitDataRenderType) {
		const output = super.generateDatabaseOutput(renderType);
		// The line below to clean up (remove) the readOnlyMode property from the database for pre-migration nodes. Setting it to null will remove the property.
		// This is part of the on the fly migration as part of the new plan archive feature.
		output['readOnlyMode'] = null;
		return output;
	}

	public databaseUpdate(data: IPlanAccessFields): void {
		// on-the-fly migration, if the readOnlyMode property is present,
		// migration is required
		if (data.accessLevel === undefined) {
			data = { ...data, ...this.migrateData(data as ILegacyIPlanAccessFields) };
		}
		super.databaseUpdate(data);
	}

	/**
	 * Migrates the data from the old PlanAccess node structure to the new one as part of the on-the-fly migration.
	 * @param preMigrationData - The data from the old PlanAccess node structure.
	 * @returns The data in the new PlanAccess node structure.
	 */
	public migrateData(preMigrationData: ILegacyIPlanAccessFields): IPlanAccessFields {
		return {
			accessModifiedBy: null,
			accessModifiedAt: null,
			accessLevel: preMigrationData.readOnlyMode ? PlanAccessLevel.locked : PlanAccessLevel.live,
		};
	}
}
